import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import SupportService from "../../services/SupportService";
import UploadAndDownloadService from "../../services/uploadAndDownloadFile";

/*
 * Support. A client company opens tickets and talks with IWARE; the super admin sees the
 * tickets of every company, answers them and moves them through their statuses. Files are
 * uploaded with the file upload service, the tickets only keep their name and url.
 */
const CATEGORIES = [
  { value: "TECHNIQUE", label: "Problème technique" },
  { value: "FACTURATION", label: "Facturation / abonnement" },
  { value: "FONCTIONNALITE", label: "Demande de fonctionnalité" },
  { value: "AUTRE", label: "Autre" },
];
const PRIORITIES = [
  { value: "BASSE", label: "Basse" },
  { value: "NORMALE", label: "Normale" },
  { value: "HAUTE", label: "Haute" },
  { value: "URGENTE", label: "Urgente" },
];
const STATUSES = [
  { value: "OUVERT", label: "Ouvert", badge: "badge-primary" },
  { value: "EN_COURS", label: "En cours", badge: "badge-warning" },
  { value: "EN_ATTENTE_CLIENT", label: "En attente du client", badge: "badge-info" },
  { value: "RESOLU", label: "Résolu", badge: "badge-success" },
  { value: "FERME", label: "Fermé", badge: "badge-secondary" },
];
const PRIORITY_BADGE = { BASSE: "badge-light", NORMALE: "badge-secondary", HAUTE: "badge-warning", URGENTE: "badge-danger" };
const label = (list, value) => (list.find((i) => i.value === value) || { label: value }).label;

const ERRORS = {
  forbidden: "Action non autorisée",
  no_company: "Aucune société n'est associée à votre compte",
  not_found: "Ticket introuvable",
  closed: "Ce ticket est fermé, rouvrez-le pour répondre",
  invalid_status: "Statut invalide",
};
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 5;
const FILE_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const formatDate = (value) => (value ? new Date(value).toLocaleString("fr-FR") : "-");

const isSuperAdmin = () => {
  try {
    const status = JSON.parse(localStorage.getItem("status"));
    return Array.isArray(status) && status.includes("SUPER_ADMIN");
  } catch (e) {
    return false;
  }
};

// uploads the picked files and returns [{ name, url }]; throws when one upload fails
const uploadFiles = async (files) => {
  const uploaded = [];
  for (const file of files) {
    const data = new FormData();
    data.append("picture", file);
    const upload = await UploadAndDownloadService.uploadFile("/retail/uploadFile/uploadFile/SupportAttachment", data);
    const url = upload.data && upload.data.success;
    if (!url) throw new Error("upload");
    uploaded.push({ name: file.name, url });
  }
  return uploaded;
};

// adds the valid files to the list, with the same limits as the server
const pickFiles = (event, current, setFiles) => {
  const picked = Array.from(event.target.files || []);
  event.target.value = "";
  const next = [...current];
  for (const file of picked) {
    if (!FILE_TYPES.includes(file.type)) {
      toast.error(`${file.name} : format accepté PDF, PNG ou JPEG`);
    } else if (file.size > MAX_FILE_BYTES) {
      toast.error(`${file.name} : 5 Mo maximum`);
    } else if (next.length >= MAX_FILES) {
      toast.error(`${MAX_FILES} pièces jointes au maximum`);
      break;
    } else {
      next.push(file);
    }
  }
  setFiles(next);
};

const FileList = ({ files, onRemove }) =>
  files.length === 0 ? null : (
    <ul className="list-unstyled small mb-2">
      {files.map((f, index) => (
        <li key={f.name + index}>
          <i className="fas fa-paperclip mr-1"></i>
          {f.name}{" "}
          <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => onRemove(index)}>
            retirer
          </button>
        </li>
      ))}
    </ul>
  );

// attachments of a ticket or a message; the file is fetched with the token, then opened
const Attachments = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  const open = (attachment) => {
    // the window is opened first, from the click, so the browser does not block it
    const win = window.open("", "_blank");
    UploadAndDownloadService.downloadFile(attachment.url)
      .then((response) => {
        const type = response.headers["content-type"] || "application/octet-stream";
        const url = URL.createObjectURL(new Blob([response.data], { type }));
        if (win) win.location.href = url;
      })
      .catch(() => {
        if (win) win.close();
        toast.error("Impossible d'ouvrir la pièce jointe");
      });
  };
  return (
    <div className="mt-2">
      {attachments.map((a, index) => (
        <button key={a.url + index} className="btn btn-sm btn-outline-dark mr-1 mb-1" onClick={() => open(a)}>
          <i className="fas fa-paperclip mr-1"></i>
          {a.name}
        </button>
      ))}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUSES.find((i) => i.value === status);
  return <span className={"badge " + (s ? s.badge : "badge-light")}>{s ? s.label : status}</span>;
};

const NewTicketDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ subject: "", description: "", category: "TECHNIQUE", priority: "NORMALE" });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const send = async () => {
    if (sending) return;
    const next = {};
    if (form.subject.trim().length < 5) next.subject = "L'objet doit contenir au moins 5 caractères";
    if (!form.description.trim()) next.description = "Décrivez votre demande";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSending(true);
    try {
      const attachments = await uploadFiles(files);
      const response = await SupportService.create({ ...form, attachments });
      if (response.data && response.data.success) {
        toast.success("Votre demande a été envoyée");
        onCreated(response.data.success);
        return;
      }
      const fieldErrors = response.data && response.data.fieldErrors;
      if (fieldErrors) setErrors(fieldErrors);
      else toast.error(ERRORS[response.data && response.data.error] || "La demande n'a pas pu être envoyée");
    } catch (e) {
      toast.error("L'envoi a échoué, vérifiez votre connexion et réessayez");
    }
    setSending(false);
  };

  return (
    <Dialog open onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nouvelle demande de support</DialogTitle>
      <DialogContent>
        <div className="form-group">
          <label htmlFor="supSubject">Objet</label>
          <input
            id="supSubject"
            className={"form-control" + (errors.subject ? " is-invalid" : "")}
            value={form.subject}
            maxLength={150}
            onChange={set("subject")}
          />
          <div className="invalid-feedback">{errors.subject}</div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="supCategory">Catégorie</label>
              <select id="supCategory" className="form-control" value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="supPriority">Priorité</label>
              <select id="supPriority" className="form-control" value={form.priority} onChange={set("priority")}>
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="supDescription">Description</label>
          <textarea
            id="supDescription"
            className={"form-control" + (errors.description ? " is-invalid" : "")}
            rows={5}
            maxLength={5000}
            value={form.description}
            onChange={set("description")}
          />
          <div className="invalid-feedback">{errors.description}</div>
        </div>
        <div className="form-group mb-0">
          <label htmlFor="supFiles">Pièces jointes (facultatif)</label>
          <FileList files={files} onRemove={(i) => setFiles(files.filter((_, index) => index !== i))} />
          <input
            id="supFiles"
            type="file"
            multiple
            accept="application/pdf,image/png,image/jpeg"
            className="form-control-file"
            onChange={(e) => pickFiles(e, files, setFiles)}
          />
          <small className="text-muted d-block mt-1">PDF, PNG ou JPEG, 5 Mo maximum, {MAX_FILES} fichiers au plus.</small>
        </div>
        {errors.attachments && <div className="text-danger small mt-2">{errors.attachments}</div>}
      </DialogContent>
      <DialogActions>
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={sending}>
          Annuler
        </button>
        <button className="btn btn-dark" onClick={send} disabled={sending}>
          <i className="fas fa-paper-plane mr-1"></i>Envoyer
        </button>
      </DialogActions>
    </Dialog>
  );
};

const TicketDetail = ({ idTicket, superAdmin, onBack, onChanged }) => {
  const [ticket, setTicket] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);

  const load = useCallback(() => {
    SupportService.get(idTicket)
      .then((response) => {
        if (response.data && response.data.success) {
          setTicket(response.data.success);
          setState("ready");
          onChanged();
        } else {
          setState("error");
        }
      })
      .catch(() => setState("error"));
  }, [idTicket, onChanged]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  useEffect(() => {
    if (bottom.current && bottom.current.scrollIntoView) bottom.current.scrollIntoView({ block: "end" });
  }, [ticket]);

  const send = async () => {
    if (busy) return;
    if (!text.trim()) {
      toast.error("Écrivez un message");
      return;
    }
    setBusy(true);
    try {
      const attachments = await uploadFiles(files);
      const response = await SupportService.reply(idTicket, { message: text.trim(), attachments });
      if (response.data && response.data.success) {
        setText("");
        setFiles([]);
        load();
      } else {
        const fieldErrors = response.data && response.data.fieldErrors;
        toast.error(
          (fieldErrors && Object.values(fieldErrors)[0]) ||
            ERRORS[response.data && response.data.error] ||
            "Le message n'a pas pu être envoyé"
        );
      }
    } catch (e) {
      toast.error("L'envoi a échoué, vérifiez votre connexion et réessayez");
    }
    setBusy(false);
  };

  const changeStatus = (status) => {
    if (busy) return;
    setBusy(true);
    SupportService.changeStatus(idTicket, status)
      .then((response) => {
        if (response.data && response.data.success) load();
        else toast.error(ERRORS[response.data && response.data.error] || "Changement impossible");
      })
      .catch(() => toast.error("Erreur : problème de connexion"))
      .finally(() => setBusy(false));
  };

  if (state === "loading") return <div className="p-3 text-muted">Chargement…</div>;
  if (state === "error" || !ticket)
    return (
      <div className="p-3">
        <span className="text-danger">Impossible de charger le ticket.</span>{" "}
        <button className="btn btn-sm btn-outline-dark" onClick={onBack}>
          Retour
        </button>
      </div>
    );

  const closed = ticket.status === "FERME";
  const canReopen = ticket.status === "RESOLU" || closed;

  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={onBack}>
            <i className="fas fa-arrow-left mr-1"></i>Retour
          </button>
          <h5 className="mb-1">
            {ticket.reference} · {ticket.subject}
          </h5>
          <div className="small text-muted">
            {superAdmin && <span>{ticket.societeTitle || `Société ${ticket.idSociete}`} · </span>}
            {ticket.creatorName} · {formatDate(ticket.creationDate)}
          </div>
          <div className="mt-2">
            <StatusBadge status={ticket.status} />{" "}
            <span className={"badge " + (PRIORITY_BADGE[ticket.priority] || "badge-light")}>
              {label(PRIORITIES, ticket.priority)}
            </span>{" "}
            <span className="badge badge-light">{label(CATEGORIES, ticket.category)}</span>
          </div>
        </div>
        <div className="text-nowrap mt-2">
          {superAdmin ? (
            <select
              className="form-control form-control-sm"
              value={ticket.status}
              disabled={busy}
              onChange={(e) => changeStatus(e.target.value)}
              aria-label="Statut du ticket"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          ) : (
            <>
              {canReopen && (
                <button className="btn btn-sm btn-outline-dark mr-1" disabled={busy} onClick={() => changeStatus("OUVERT")}>
                  Rouvrir
                </button>
              )}
              {!closed && (
                <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => changeStatus("FERME")}>
                  Fermer le ticket
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="card-body" style={{ maxHeight: "55vh", overflowY: "auto" }}>
        <div className="border rounded p-3 mb-3 bg-light">
          <div style={{ whiteSpace: "pre-wrap" }}>{ticket.description}</div>
          <Attachments attachments={ticket.attachments} />
        </div>
        {ticket.messages.map((m) => (
          <div key={m.id} className={"d-flex mb-3 " + (m.authorType === "SUPPORT" ? "" : "justify-content-end")}>
            <div
              className={"rounded p-2 " + (m.authorType === "SUPPORT" ? "bg-light border" : "bg-dark text-white")}
              style={{ maxWidth: "80%" }}
            >
              <div className="small mb-1" style={{ opacity: 0.75 }}>
                {m.authorType === "SUPPORT" ? "Support IWARE" : m.authorName} · {formatDate(m.creationDate)}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{m.message}</div>
              <Attachments attachments={m.attachments} />
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <div className="card-footer">
        {closed ? (
          <span className="text-muted">Ce ticket est fermé.{!superAdmin && " Rouvrez-le pour répondre."}</span>
        ) : (
          <>
            <textarea
              className="form-control mb-2"
              rows={3}
              maxLength={5000}
              placeholder="Votre message"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <FileList files={files} onRemove={(i) => setFiles(files.filter((_, index) => index !== i))} />
            <div className="d-flex justify-content-between align-items-center">
              <input
                type="file"
                multiple
                accept="application/pdf,image/png,image/jpeg"
                aria-label="Ajouter des pièces jointes"
                onChange={(e) => pickFiles(e, files, setFiles)}
              />
              <button className="btn btn-dark" onClick={send} disabled={busy}>
                <i className="fas fa-paper-plane mr-1"></i>Envoyer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Support = () => {
  const superAdmin = isSuperAdmin();
  const [tickets, setTickets] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    SupportService.list(filter)
      .then((response) => {
        if (response.data && Array.isArray(response.data.success)) {
          setTickets(response.data.success);
          setState("ready");
        } else {
          setState("error");
        }
      })
      .catch(() => setState("error"));
  }, [filter]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <ToastContainer />
      <section className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 className="mb-1">
              <i className="far fa-life-ring mr-2"></i>Support
            </h4>
            <small className="text-muted">
              {superAdmin ? "Demandes de support des clients" : "Vos demandes de support auprès d'IWARE"}
            </small>
          </div>
          {!superAdmin && !selected && (
            <button className="btn btn-dark" onClick={() => setCreating(true)}>
              <i className="fas fa-plus mr-1"></i>Nouvelle demande
            </button>
          )}
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {selected ? (
            <TicketDetail idTicket={selected} superAdmin={superAdmin} onBack={() => setSelected(null)} onChanged={load} />
          ) : (
            <div className="card mt-3">
              <div className="card-header">
                <select
                  className="form-control form-control-sm"
                  style={{ maxWidth: 240 }}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Filtrer par statut"
                >
                  <option value="">Tous les statuts</option>
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="card-body table-responsive p-0">
                {state === "loading" && <div className="p-3 text-muted">Chargement…</div>}
                {state === "error" && (
                  <div className="p-3">
                    <span className="text-danger">Impossible de charger les tickets.</span>{" "}
                    <button className="btn btn-sm btn-outline-dark" onClick={load}>
                      Réessayer
                    </button>
                  </div>
                )}
                {state === "ready" && tickets.length === 0 && <div className="p-3 text-muted">Aucun ticket.</div>}
                {state === "ready" && tickets.length > 0 && (
                  <table className="table table-hover">
                    <thead className="thead-light">
                      <tr>
                        <th>Référence</th>
                        <th>Objet</th>
                        {superAdmin && <th>Société</th>}
                        <th>Catégorie</th>
                        <th>Priorité</th>
                        <th>Statut</th>
                        <th>Dernière activité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => setSelected(t.id)}>
                          <td className="text-nowrap">
                            {t.reference}
                            {t.unread && <span className="badge badge-danger ml-1">nouveau</span>}
                          </td>
                          <td style={{ fontWeight: t.unread ? 600 : 400 }}>{t.subject}</td>
                          {superAdmin && <td>{t.societeTitle || `Société ${t.idSociete}`}</td>}
                          <td>{label(CATEGORIES, t.category)}</td>
                          <td>
                            <span className={"badge " + (PRIORITY_BADGE[t.priority] || "badge-light")}>
                              {label(PRIORITIES, t.priority)}
                            </span>
                          </td>
                          <td>
                            <StatusBadge status={t.status} />
                          </td>
                          <td className="text-nowrap">{formatDate(t.lastActivityDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {creating && (
        <NewTicketDialog
          onClose={() => setCreating(false)}
          onCreated={(ticket) => {
            setCreating(false);
            load();
            setSelected(ticket.id);
          }}
        />
      )}
    </div>
  );
};

export default Support;
